import { CheckIcon } from '@heroicons/react/24/solid';
import React, { FunctionComponent, HTMLAttributes, useState } from 'react';
import { Account } from '../../services/models/account';
import { Product } from '../../services/models/product';
import { SubscriptionInterval } from '../../services/models/subscription';
import { launchIntercom } from '../../utils/Intercom';

enum BuyerIntent {
  None = 'None',
  Upgrade = 'Upgrade',
  Downgrade = 'Downgrade',
  ChangeInterval = 'Change Interval',
}

interface ProductCardProps {
  account: Account;
  active: boolean;
  mostPopular: boolean;
  product: Product;
  isBilledMonthly: boolean;
  onSelected: (planId: string, product: Product) => void;
  showsConfirmation: boolean;
  showsLoading: boolean;
  handleConfirmationChange: (confirmation: boolean) => void;
}

const ProductCard: FunctionComponent<ProductCardProps> = ({
  account,
  active,
  product,
  isBilledMonthly,
  mostPopular,
  onSelected,
  showsLoading,
  handleConfirmationChange,
  showsConfirmation,
}) => {
  const [buyerIntent, setBuyerIntent] = useState(BuyerIntent.None);

  const activeBriefsCount = account.metadata?.activeReportsCount || 0;
  const isTrial = account.subscription?.trial;
  const isCurrentPlanCanceled = account.subscription?.canceled;
  const requiresConfirmation =
    account.subscription?.planId !== '' || product.features.briefs < activeBriefsCount;
  const productFeatures = product.features;
  const periodicity = isBilledMonthly ? 'month' : 'year';
  const productPlan = product.plans.find(p => p.periodicity === periodicity);

  const handleBuyerIntent = (intent: BuyerIntent) => () => {
    if (requiresConfirmation) {
      setBuyerIntent(intent);
      handleConfirmationChange(true);
      return;
    }

    executeBuyerIntent();
  };

  const executeBuyerIntent = () => {
    onSelected(productPlan!.planId, product);
  };

  const getActionButton = () => {
    const canUpgrade =
      isTrial ||
      productFeatures.briefs.toString() === 'Unlimited' ||
      productFeatures.briefs > account.product.features.briefs;

    const isCurrentPlanMonthly = account.subscription.interval !== SubscriptionInterval.Yearly;
    const sameAsCurrentProduct = product.productId === account.product.productId;
    const haveDifferentInterval = isCurrentPlanMonthly !== isBilledMonthly;

    if (isCurrentPlanCanceled) {
      return <ActionButton title="Buy" isLoading={showsLoading} onClick={executeBuyerIntent} />;
    }

    if (canUpgrade) {
      return (
        <ActionButton
          title="Upgrade"
          isLoading={showsLoading}
          onClick={handleBuyerIntent(BuyerIntent.Upgrade)}
        />
      );
    }

    if (sameAsCurrentProduct) {
      if (haveDifferentInterval) {
        return (
          <ActionButton
            title={`Change to ${
              isCurrentPlanMonthly ? SubscriptionInterval.Yearly : SubscriptionInterval.Monthly
            }`}
            isLoading={showsLoading}
            onClick={handleBuyerIntent(BuyerIntent.ChangeInterval)}
          />
        );
      } else {
        return (
          <button
            className="mt-8 block w-full bg-white border border-gray-300 rounded-md py-2 text-sm font-semibold text-gray-700 text-center disabled:opacity-50"
            disabled
          >
            Current Plan
          </button>
        );
      }
    }

    // Prevent Yearly plan downgrades
    if (!isBilledMonthly) {
      return (
        <button
          className="mt-8 block w-full bg-white border border-gray-300 rounded-md py-2 text-sm font-semibold text-gray-700 text-center disabled:opacity-50"
          onClick={launchIntercom}
        >
          Contact Us
        </button>
      );
    }

    return (
      <ActionButton
        isLoading={showsLoading}
        title="Change Plan"
        onClick={handleBuyerIntent(BuyerIntent.Downgrade)}
      />
    );
  };

  if (showsConfirmation) {
    return (
      <ConfirmationScreen
        intent={buyerIntent}
        product={product}
        account={account}
        isBilledMonthly={isBilledMonthly}
        showsLoading={showsLoading}
        onChangePlan={executeBuyerIntent}
        onCancelChange={() => handleConfirmationChange(false)}
      />
    );
  }

  return (
    <div
      key={product.name}
      className="relative p-6 bg-white border border-gray-200 rounded-2xl shadow-xs flex flex-col"
    >
      <div className="flex-1">
        <h3 className="text-xl font-semibold text-gray-900">{product.name}</h3>

        {isTrial && product.mostPopular ? (
          <p className="absolute top-0 py-1.5 px-4 bg-primary rounded-full text-xs font-semibold uppercase tracking-wide text-white transform -translate-y-1/2">
            Most popular
          </p>
        ) : null}

        <p className="mt-4 flex items-baseline text-gray-900">
          <span className="text-4xl font-extrabold tracking-tight">
            ${productPlan!.monthlyPrice}
          </span>
          <span className="ml-1 text-sm font-medium text-gray-500">/month</span>
        </p>

        <p className="mt-1 text-sm leading-6 text-gray-400">
          {!isBilledMonthly ? 'Billed annually' : ' '}&nbsp;
        </p>

        {/* <p className="mt-6 text-gray-500">{tier.description}</p> */}

        {/* Feature list */}
        <ul className="mt-4 space-y-6">
          {/*{product.features.map((feature) => (*/}
          {/*  <li key={feature} className="flex">*/}
          {/*    <CheckIcon className="shrink-0 w-6 h-6 text-indigo-500" aria-hidden="true" />*/}
          {/*    <span className="ml-3 text-gray-500">{feature}</span>*/}
          {/*  </li>*/}
          {/*))}*/}

          <li className="flex">
            <CheckIcon className="shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
            <span className="ml-3 text-sm text-gray-500">{productFeatures.briefs} Briefs</span>
          </li>
          <li className="flex space-x-3">
            <CheckIcon className="shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
            <span className="text-sm text-gray-500">
              Up to {productFeatures.recipientsPerBrief} recipient
              {productFeatures.recipientsPerBrief > 1 ? 's' : ''}
            </span>
          </li>
          <li className="flex space-x-3">
            <CheckIcon className="shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
            <span className="text-sm text-gray-500">
              Up to {productFeatures.keywordsPerBrief} Keywords
            </span>
          </li>
        </ul>
      </div>
      {getActionButton()}
    </div>
  );
};

export default ProductCard;

interface ConfirmationScreenProps {
  intent: BuyerIntent;
  product: Product;
  account: Account;
  isBilledMonthly: boolean;
  onChangePlan: () => void;
  onCancelChange: () => void;
  showsLoading: boolean;
}

const ConfirmationScreen: React.FC<ConfirmationScreenProps> = ({
  intent,
  product,
  account,
  isBilledMonthly,
  onChangePlan,
  onCancelChange,
  showsLoading,
}) => {
  const activeBriefsCount = account.metadata?.activeReportsCount || 0;

  return (
    <div className="relative p-6 bg-white border border-secondary rounded-2xl shadow-xs flex flex-col">
      <div className="flex-1">
        <h3 className="text-xl font-extrabold text-gray-900">{intent}</h3>
        <div className="mt-4">
          {/* Prevent accounts from downgrading if not possible */}
          {activeBriefsCount <= product.features.briefs ? (
            <>
              <p className="text-gray-900">
                You are about to change to our <span className="font-bold">{product.name}</span>{' '}
                plan, billed {isBilledMonthly ? 'Monthly' : 'Annually'}.
              </p>
              <p className="text-gray-900">
                We'll credit you for any unused portion of your current plan.
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-900">
                You have <span className="font-bold">{activeBriefsCount}</span> active News Briefs.
                Please, bring the number of active briefs to{' '}
                <span className="font-bold">{product.features.briefs}</span> before downgrading your
                account.
              </p>
            </>
          )}
        </div>
      </div>
      <div>
        {activeBriefsCount <= product.features.briefs && (
          <ActionButton title="Change Plan" isLoading={showsLoading} onClick={onChangePlan} />
        )}

        <button
          type="button"
          className="mt-3 py-2 w-full block rounded-md border border-gray-300 shadow-xs bg-white text-base font-medium text-gray-700 hover:bg-gray-50"
          onClick={onCancelChange}
          disabled={showsLoading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

interface ActionButtonProps extends HTMLAttributes<HTMLButtonElement> {
  title: string;
  isLoading?: boolean;
}

function ActionButton(props: ActionButtonProps) {
  const { title, isLoading, ...rest } = props;
  return (
    <button
      {...rest}
      disabled={isLoading}
      className="mt-8 w-full inline-flex items-center justify-center bg-secondary rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-secondary-dark disabled:opacity-50"
    >
      {isLoading && (
        <svg
          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {title}
    </button>
  );
}
